/**
 * How much room an attachment viewer gets, and where that room comes from.
 *
 * Pure, and separate from `EmailQuickPreview.tsx`, because these are the rules
 * that were wrong: the full-size dialog grew to 90vh while every viewer inside
 * it stayed capped, so the Excel sheet carrying the supplier's accept/reject
 * lines was read through a 420px window in a near-full-screen modal. Rules that
 * decide how much of a claim someone can see at once are worth testing; JSX
 * isn't.
 */

/**
 * Never hand a viewer less than this, however small the box measures.
 *
 * A ResizeObserver reports 0 for a box that is display:none or not yet laid
 * out, and a viewer told it has 0 (or, after subtracting padding, a negative)
 * renders as a sliver with no way back — the measurement recovers on the next
 * frame but the viewer may already have sized its grid.
 */
export const MIN_VIEWER_HEIGHT_PX = 240;

/**
 * The box a viewer sits in: capped when embedded in an attachment row, taking
 * all the height its parent gives in the full-size dialog.
 *
 * One rule shared by every viewer kind, so the image, PDF and Excel branches
 * can't drift apart again.
 */
export function viewerBoxClass(fill: boolean): string {
  return fill
    ? 'min-h-0 flex-1 overflow-auto p-3'
    : 'max-h-[420px] overflow-auto p-3';
}

/**
 * The pixel height to hand a viewer, or `undefined` to leave it on its default.
 *
 * It must be a NUMBER, not `'100%'`: `XlsxViewer` derives its grid height by
 * subtracting its own chrome from a numeric height and falls back to a fixed
 * 406px for anything else — so a percentage would look exactly like the fixed
 * height this exists to replace.
 *
 * `boxHeight` is passed through as-is because it comes from
 * `ResizeObserverEntry.contentRect`, which is the CONTENT box: the `p-3` on the
 * viewer box is already excluded. Subtracting it again (as this did on first
 * write) leaves the sheet 24px short of the room it actually has.
 *
 * `undefined` outside fill mode is deliberate: the inline attachment row wants
 * every viewer on its own 450px default, and passing a measured height there
 * would make a thumbnail as tall as its container.
 */
export function resolveViewerHeight(
  fill: boolean,
  boxHeight: number | null,
): number | undefined {
  if (!fill || boxHeight == null) return undefined;
  return Math.max(MIN_VIEWER_HEIGHT_PX, Math.round(boxHeight));
}
